import css from './useMessage.module.less';
import { version } from '@/../package.json';
import { useAuth } from '@/auth/useAuth';
import { t, useTranslator } from '@/translations/translator';
import { ChevronRight, Close } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, Collapse, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import { errorStackOnly, errorToString } from '@whiz-cart/node-shared/errorToString';
import isTruthy from '@whiz-cart/node-shared/helpers/isTruthy';
import type { AccessToken } from '@whiz-cart/ui-shared/auth/authTypes';
import { CopyOnClick } from '@whiz-cart/ui-shared/copyOnClick/copyOnClick.component.tsx';
import _ from 'lodash';
import { OptionsObject, SnackbarKey, useSnackbar } from 'notistack';
import { ReactNode, useCallback, useEffect, useState } from 'react';

export type KnownError<T extends object = any> = [match: Record<string, unknown>, message: string | ((error: T) => string)];

export function matchError(error: unknown, knownErrors: KnownError[] = []) {
    if (error instanceof Object) {
        const data = (error as any).data;

        const knownError = knownErrors.find(([match]) => {
            return _.isMatch(error, match) || (data instanceof Object && _.isMatch(data, match));
        });

        if (knownError) {
            const message = knownError[1] instanceof Function ? knownError[1](error) : knownError[1];

            if (error instanceof Error) {
                const copy = new Error(message, { cause: error });
                copy.stack = error.stack;
                return copy;
            }

            return message;
        }
    }

    return error;
}

type Props = { error?: unknown; title?: string; close: () => void };

function SnackbarErrorContent({ title, error, close }: Props) {
    const [open, setOpen] = useState(false);
    const { tokenDetails } = useAuth();

    if (!error) {
        return null;
    }

    const userFriendlyError = title ?? errorToString(error);
    const errorDetails = getErrorDetails(error, tokenDetails);
    const stack = errorStackOnly(error);

    return (
        <div
            className={css.errorContainer}
            css={{
                display: 'grid',
                grid: `
                    "title copy close" max-content
                    "stack-button stack-button stack-button" max-content
                    "stack stack stack" minmax(0, 1fr)
                    / minmax(0, 1fr) max-content max-content
                `,
                justifyItems: 'start',
                alignItems: 'center',

                '@media (max-width: 600px)': {
                    grid: `
                        "title close" max-content
                        "copy copy" max-content
                        "stack-button stack-button" max-content
                        "stack stack" minmax(0, 1fr)
                        / minmax(0, 1fr) max-content
                    `,
                },

                pre: {
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '10rem',
                    overflow: 'auto',
                },

                'p:not(:first-of-type)': {
                    ml: 2,
                },
            }}
        >
            <pre className={css.errorTitle} css={{ gridArea: 'title' }}>
                {userFriendlyError.split('\n').map((line, index) => (
                    <p key={index}>{line}&nbsp;</p>
                ))}
            </pre>

            <CopyOnClick value={errorDetails} css={{ gridArea: 'copy' }}>
                <Button size="small" variant="outlined" color="inherit">
                    {t('clicktoCopy')}
                </Button>
            </CopyOnClick>

            <IconButton color="inherit" onClick={() => close()} css={{ gridArea: 'close' }}>
                <Close />
            </IconButton>

            {stack && (
                <>
                    <Button
                        variant="text"
                        color="inherit"
                        startIcon={
                            <ChevronRight
                                sx={{
                                    transition: 'all 300ms',
                                    transform: `rotate3d(0, 0, 1, ${open ? '90deg' : '0'})`,
                                }}
                            />
                        }
                        onClick={() => setOpen(!open)}
                        css={{ gridArea: 'stack-button' }}
                    >
                        Stack
                    </Button>

                    <Collapse in={open} css={{ gridArea: 'stack' }}>
                        <Box
                            sx={{
                                mt: 2,
                                display: 'grid',
                            }}
                        >
                            <Box
                                sx={{
                                    minWidth: 0,
                                    maxHeight: '20rem',
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre',
                                    background: '@lightGrayColor',
                                    overflow: 'auto',
                                }}
                            >
                                {stack}
                            </Box>
                        </Box>
                    </Collapse>
                </>
            )}
        </div>
    );
}

export function useShowError() {
    const { showMessage, closeMessage } = useShowMessage();

    const showError = useCallback(
        (error?: unknown | unknown[], knownErrors?: KnownError[], title?: string) => {
            const errors = (Array.isArray(error) ? error : [error]).filter(isTruthy);
            if (!errors.length) {
                return;
            }
            const keys: SnackbarKey[] = [];
            errors.forEach((e: any) => {
                console.error(e);
                const key = showMessage(
                    <SnackbarErrorContent
                        title={title}
                        error={matchError(e, knownErrors)}
                        close={() => {
                            closeMessage(key);
                        }}
                    />,
                    { variant: 'error' },
                );
                keys.push(key);
            });
            return keys;
        },
        [showMessage, closeMessage],
    );

    return { showError, closeError: closeMessage };
}

export function useShowMessage() {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const closeMessage = useCallback(
        (key?: SnackbarKey) => {
            closeSnackbar(key);
        },
        [closeSnackbar],
    );

    const showMessage = useCallback(
        (content: ReactNode, options?: OptionsObject) => {
            const key = `key-${Math.random().toString(16).slice(2)}`;
            return enqueueSnackbar(content, {
                persist: true,
                key,
                ...options,
            });
        },
        [enqueueSnackbar],
    );

    return { showMessage, closeMessage };
}

export function useShowSuccess() {
    const { showMessage, closeMessage } = useShowMessage();
    const t = useTranslator();

    function showSuccess({
        message = t('success'),
        action,
    }: {
        message?: ReactNode | undefined;
        /** Clicking it also closes the message. */
        action?: { label: ReactNode; onClick: () => void };
    } = {}) {
        if (!message) {
            return;
        }
        const key = `key-${Math.random().toString(16).slice(2)}`;
        return showMessage(
            <>
                <CheckCircleIcon className={css.checkIcon} />
                {message}
            </>,
            {
                variant: 'success',
                className: css.successMessage,
                autoHideDuration: action ? 8000 : 3000,
                persist: false,
                key,
                action: (
                    <>
                        {action && (
                            <Button
                                variant="text"
                                color="inherit"
                                sx={{ color: 'white', fontWeight: 'bold' }}
                                onClick={() => {
                                    action.onClick();
                                    closeMessage(key);
                                }}
                            >
                                {action.label}
                            </Button>
                        )}
                        <IconButton onClick={() => closeMessage(key)} style={{ color: 'white' }}>
                            <Close />
                        </IconButton>
                    </>
                ),
            },
        );
    }

    return { showSuccess, closeSuccess: closeMessage };
}

export function useErrorMessage(error?: unknown | unknown[], knownErrors?: KnownError[], title?: string) {
    const { showError, closeError } = useShowError();

    useEffect(() => {
        const errorKey = showError(error, knownErrors, title);
        return () => {
            errorKey?.forEach((k) => k && closeError(k));
        };
    }, [error]);
}

export function useCloseOnExit(closeError: () => void) {
    useEffect(() => {
        return () => {
            closeError();
        };
    }, []);
}

export function getErrorDetails(error: unknown, tokenDetails?: AccessToken) {
    return [
        `Time: ${new Date().toISOString()}`,
        typeof window !== 'undefined' ? `URL: ${window.location.href}` : undefined,
        `User agent: ${navigator.userAgent}`,
        `Version: ${version}`,
        tokenDetails && `User: ${tokenDetails.unique_name}`,
        '---',
        errorToString(error),
        errorStackOnly(error),
    ]
        .filter(isTruthy)
        .join('\n');
}
